import {Import, ListChecks, Plus, X} from 'lucide-react';
import * as React from 'react';
import {useHotkeys} from 'react-hotkeys-hook';
import {AmountDisplay} from 'src/AmountDisplay';
import {Button} from 'src/Button';
import ActionHeader from 'src/components/ActionHeader';
import {totalsByCurrency} from 'src/lib/currencyTotals';
import {useModals} from 'src/Modals';
import {PopoverMenu} from 'src/PopoverMenu';
import {
  PopoverMenuDivider,
  PopoverMenuGroupLabel,
} from 'src/PopoverMenu/views';
import PopoverMenuItem from 'src/PopoverMenuItem';
import {HeaderSyncButton} from 'src/RawTransactionListView/HeaderSyncButton';
import {RawSelectionBar} from 'src/RawTransactionListView/RawSelectionBar';
import type {BasicRawTransactionFragment} from '@transactions/graphql';
import type {useRawRowGestures} from 'src/RawTransactionListView/useRawRowGestures';
import type {useRawSelectionActions} from 'src/RawTransactionListView/useRawSelectionActions';
import type {SelectionState} from 'src/useSelectionState';

type Props = {
  selectionState: SelectionState<BasicRawTransactionFragment>;
  /** The list's selection actions (useRawSelectionActions). */
  actions: ReturnType<typeof useRawSelectionActions>;
  /**
   * The list's row gestures. Given, the list is a browse list: the selection
   * lives in the floating selection bar, and the header keeps its filters
   * and offers Select. Left out (the reconcile tab, where tapping rows is
   * how a reconciliation is built), the header itself turns into the
   * selection summary with the actions in its overflow menu.
   */
  gestures?: ReturnType<typeof useRawRowGestures>;
  filterContent?: React.ReactNode;
  // Extra controls rendered at the start of the action group (before the sync
  // button and overflow menu). The reconcile tab uses this to keep Record
  // balance / Add transaction on the same row as its other actions; the
  // Activity page puts its search box here so it shares a row with them
  // instead of costing one of its own.
  extraActions?: React.ReactNode;
  // Set when the list is scoped to one account, so created rows default to it.
  accountId?: string;
  // The review workflow embeds under the inbox, which already has a sync
  // button in its own header — hide the duplicate there.
  showSyncButton?: boolean;
  // Non-selection actions (add, import) — the inbox surfaces these at the
  // top level, so the review workflow hides them here.
  showAddActions?: boolean;
  // Override for the mobile layout (see ActionHeader). Defaults to stacking
  // while browsing, where the filter row can wrap to several lines. A caller
  // with a single narrow filter — the review page's lone Dates pill — passes
  // false so the filter and the actions share one row instead of costing two.
  stackActionsOnMobile?: boolean;
};

export const Header = ({
  selectionState,
  actions: selectionActions,
  gestures,
  filterContent,
  extraActions,
  accountId,
  showSyncButton = true,
  showAddActions = true,
  stackActionsOnMobile,
}: Props) => {
  const {selected} = selectionState;
  const sums = totalsByCurrency(selected);

  const selectedCount = selected.length;
  const hasSelected = selectedCount > 0;
  const browse = gestures !== undefined;
  const selecting = gestures?.selecting ?? hasSelected;

  const total = hasSelected ? (
    // One total per currency, so this group can be arbitrarily wide. It wraps
    // internally too, so a three-currency selection stacks whole amounts
    // rather than pushing the last one out of the header.
    <span className="inline-flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
      {sums.map((sum) => (
        <AmountDisplay key={sum.currency} variant="plain" amount={sum} />
      ))}
    </span>
  ) : null;

  const modals = useModals();
  const {
    anyModalOpen,
    canCreateWithBenefit,
    openCreateTransactionModal,
    handleAddTransaction,
    handleImport,
    selectionMenuItems,
  } = selectionActions;

  const clearSelection = gestures?.stopSelecting ?? selectionState.clearSelection;

  useHotkeys(
    'Escape',
    () => {
      if (anyModalOpen) return;
      if (selecting) clearSelection();
    },
    [selecting, anyModalOpen, clearSelection],
  );

  useHotkeys(
    'c',
    (e) => {
      if (anyModalOpen) return;
      e.preventDefault();
      openCreateTransactionModal();
    },
    [selected, selectionActions.canCreateTransaction, anyModalOpen],
  );

  useHotkeys(
    'p',
    (e) => {
      if (anyModalOpen) return;
      e.preventDefault();
      openCreateTransactionModal();
    },
    [selected, selectionActions.canCreateTransaction, anyModalOpen],
  );

  useHotkeys(
    'b',
    (e) => {
      if (anyModalOpen) return;
      e.preventDefault();
      if (!canCreateWithBenefit) return;
      openCreateTransactionModal({expandBenefit: true});
    },
    [selected, canCreateWithBenefit, anyModalOpen],
  );

  // Block-level flex (not inline-flex) so the row is bounded by the header
  // slot rather than shrink-to-fit — otherwise, in a header whose actions sit
  // on the same row (the review page), the summary runs under the action icons
  // instead of staying inside its slot.
  //
  // Bounded, it has to give somewhere. It wraps first: a total is a number and
  // clipping one is worse than a taller header, and a mixed-currency selection
  // renders one per currency, so the summary can outgrow any slot. The count is
  // the only part that may truncate, and only once wrapping has run out of
  // room — amounts are nowrap throughout, so each stays whole on its line.
  const headerContent = !browse && hasSelected ? (
    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 gap-y-1 overflow-hidden pr-2 font-semibold whitespace-nowrap">
      <Button
        variant="ghost"
        className="text-muted-foreground hover:text-foreground h-8 w-8 shrink-0 p-0"
        onClick={selectionState.clearSelection}
        title="Clear selection"
      >
        <X size={18} />
      </Button>
      <button
        type="button"
        className="min-w-0 cursor-pointer truncate"
        onClick={selectionState.clearSelection}
        title="Clear selection"
      >
        {selectedCount} Selected
      </button>
      {total}
    </div>
  ) : (
    (filterContent ?? null)
  );

  const addItems = showAddActions && (
    <>
      <PopoverMenuGroupLabel>Add</PopoverMenuGroupLabel>
      <PopoverMenuItem
        onSelect={() =>
          modals.createRawTransactionModal.openModal({
            defaultAccountId: accountId,
            onCreate: handleAddTransaction,
          })
        }
        iconLeft={<Plus />}
      >
        Add transaction
      </PopoverMenuItem>
      <PopoverMenuItem
        onSelect={() =>
          modals.importTransactionsModal.openModal({
            onImport: handleImport,
          })
        }
        iconLeft={<Import />}
      >
        Import
      </PopoverMenuItem>
    </>
  );

  const actions = browse ? (
    <>
      {extraActions}
      <Button
        variant="outline"
        className="shrink-0 gap-1.5 text-sm max-md:px-2.5"
        onClick={selecting ? clearSelection : gestures.startSelecting}
        aria-pressed={selecting}
        aria-label={selecting ? 'Done selecting' : 'Select rows'}
      >
        <ListChecks className="h-4 w-4" />
        <span className="max-md:hidden">{selecting ? 'Done' : 'Select'}</span>
      </Button>
      {showSyncButton && <HeaderSyncButton />}
      {addItems && <PopoverMenu>{addItems}</PopoverMenu>}
    </>
  ) : (
    <>
      {extraActions}
      {showSyncButton && <HeaderSyncButton />}
      <PopoverMenu>
        {selectionMenuItems}
        {addItems && (
          <>
            <PopoverMenuDivider />
            {addItems}
          </>
        )}
      </PopoverMenu>
    </>
  );
  const header = (
    <ActionHeader
      content={headerContent}
      actions={actions}
      // Browse mode has wrapping filters that need their own row on mobile;
      // the reconcile tab has no filters, so its actions stay inline — until a
      // selection puts the count and total where the (empty) filter row was.
      // That content is nowrap and shrink-to-fit, so on a phone it overlaps the
      // actions rather than truncating; giving them their own row is the fix.
      stackActionsOnMobile={stackActionsOnMobile ?? (browse || hasSelected)}
    />
  );
  return (
    <>
      {header}
      {gestures && (
        <RawSelectionBar
          actions={selectionActions}
          open={gestures.selecting}
          onCancel={gestures.stopSelecting}
        />
      )}
    </>
  );
};
